/**
 * components/branch/BranchOffChip.js
 *
 * "Off at 2 of 5 branches" on a catalogue row (4 Oct 2026): how many of the
 * vendor's locations cannot sell the product right now, because it is
 * switched off there or sold out there. Read once per screen from
 * GET /api/catalog/branches/:owner/states?location=all, which lists only the
 * rows that differ from head office.
 *
 * "all" is head office's view, so a branch member gets no chips (their own
 * branch is on the Branches tab), and a vendor without branches gets none.
 */
import React, { useCallback, useEffect, useState } from "react";
import { apiBranchStates } from "../../api/branchCatalog.js";
import { useVendorLocations } from "../../context/BranchScopeContext.js";
import { useVendorAccess } from "../../hooks/useVendorAccess.js";
import { ALL } from "../../utilities/vendorScope.js";
import "./branch.css";

/** productId -> number of locations where it cannot be ordered now. */
export const offCountsOf = (rows = []) => {
  const byProduct = new Map();
  for (const row of rows || []) {
    const off = row?.available === false || row?.soldOut?.live === true;
    if (!off) continue;
    const id = String(row.productId);
    if (!byProduct.has(id)) byProduct.set(id, new Set());
    byProduct.get(id).add(String(row.location));
  }
  const counts = new Map();
  for (const [id, set] of byProduct) counts.set(id, set.size);
  return counts;
};

/**
 * @returns {{ counts: Map<string, number>, total: number, refresh: () => void }}
 */
export const useBranchOffCounts = (vendorUserId) => {
  const { isBranchScoped } = useVendorAccess();
  const { hasBranches, locations } = useVendorLocations(vendorUserId);
  const [counts, setCounts] = useState(() => new Map());
  const enabled = Boolean(vendorUserId) && hasBranches && !isBranchScoped;

  const refresh = useCallback(async () => {
    if (!enabled) {
      setCounts(new Map());
      return;
    }
    try {
      const { data } = await apiBranchStates(vendorUserId, ALL);
      setCounts(offCountsOf(data?.rows));
    } catch (err) {
      // The chip is a hint; the Branches tab is the place to look.
      setCounts(new Map());
    }
  }, [enabled, vendorUserId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { counts, total: enabled ? locations.length : 0, refresh };
};

export default function BranchOffChip({ count, total }) {
  if (!count || !total) return null;
  return (
    <span
      className="lk-pill lk-branch-chip lk-branch-chip--off"
      title="Switched off or sold out at these branches. See the Branches tab."
      style={{ marginLeft: 6 }}
    >
      Off at {count} of {total} branches
    </span>
  );
}
